import { Card, CardContent } from '@/common/ui/card'
import {
	Users,
	CheckCircle2,
	TrendingUp,
	Banknote,
	RefreshCw,
} from 'lucide-react'
import {
	formatConversionRate,
	formatConversionAmount,
	formatNumber,
	getTotalReactivationConversions,
	getBestPerformingCategory,
} from '../../utils/formatConversionData'

import type { ChannelAnalytics } from '../../types'

interface ReactivationOverviewCardsProps {
	analytics: ChannelAnalytics | undefined
	isLoading?: boolean
}

interface KPICardProps {
	title: string
	value: string
	subtitle?: string
	icon: React.ReactNode
	isLoading?: boolean
	highlight?: boolean
}

function KPICard({
	title,
	value,
	subtitle,
	icon,
	isLoading,
	highlight,
}: KPICardProps) {
	if (isLoading) {
		return (
			<Card>
				<CardContent className="p-6">
					<div className="flex items-start justify-between">
						<div className="space-y-2">
							<div className="h-4 w-24 animate-pulse rounded bg-muted" />
							<div className="h-8 w-32 animate-pulse rounded bg-muted" />
							<div className="h-3 w-20 animate-pulse rounded bg-muted" />
						</div>
						<div className="h-10 w-10 animate-pulse rounded-full bg-muted" />
					</div>
				</CardContent>
			</Card>
		)
	}

	return (
		<Card
			className={
				highlight ? 'border-green-500 bg-green-50/50 dark:bg-green-950/20' : ''
			}
		>
			<CardContent className="p-6">
				<div className="flex items-start justify-between">
					<div className="space-y-1">
						<p className="text-sm font-medium text-muted-foreground">{title}</p>
						<p className="text-2xl font-bold tracking-tight">{value}</p>
						{subtitle && (
							<p className="text-xs text-muted-foreground">{subtitle}</p>
						)}
					</div>
					<div
						className={`rounded-full p-2.5 ${
							highlight
								? 'bg-green-100 text-green-600 dark:bg-green-900 dark:text-green-400'
								: 'bg-muted text-muted-foreground'
						}`}
					>
						{icon}
					</div>
				</div>
			</CardContent>
		</Card>
	)
}

export default function ReactivationOverviewCards({
	analytics,
	isLoading,
}: ReactivationOverviewCardsProps) {
	const categoryMetrics = analytics?.depositConversionByCategory
	const reactivationTotals = getTotalReactivationConversions(categoryMetrics)
	const bestCategory = getBestPerformingCategory(categoryMetrics)

	// Calculate delivery rate
	const deliveryRate =
		analytics?.totalRecipients && analytics.totalRecipients > 0
			? (analytics.successfulDeliveries / analytics.totalRecipients) * 100
			: 0

	return (
		<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
			{/* Total Recipients */}
			<KPICard
				title="ผู้รับข้อความทั้งหมด"
				value={formatNumber(analytics?.totalRecipients ?? 0)}
				subtitle={`ส่งสำเร็จ ${formatNumber(analytics?.successfulDeliveries ?? 0)} คน`}
				icon={<Users className="h-5 w-5" />}
				isLoading={isLoading}
			/>

			{/* Delivery Rate */}
			<KPICard
				title="อัตราส่งสำเร็จ"
				value={formatConversionRate(deliveryRate)}
				subtitle={`ล้มเหลว ${formatNumber(analytics?.failedDeliveries ?? 0)} คน`}
				icon={<CheckCircle2 className="h-5 w-5" />}
				isLoading={isLoading}
			/>

			{/* Reactivation Conversions */}
			<KPICard
				title="Reactivation ฝากเงิน"
				value={formatNumber(reactivationTotals.count)}
				subtitle={formatConversionAmount(reactivationTotals.amount)}
				icon={<RefreshCw className="h-5 w-5" />}
				isLoading={isLoading}
				highlight
			/>

			{/* Total Deposit Amount */}
			<KPICard
				title="ยอดฝากรวม"
				value={formatConversionAmount(analytics?.totalDepositAmount ?? 0)}
				subtitle={`${formatNumber(analytics?.totalDepositConversions ?? 0)} คน (unique)`}
				icon={<Banknote className="h-5 w-5" />}
				isLoading={isLoading}
			/>

			{/* Best Performing Category */}
			<KPICard
				title="กลุ่มที่ดีที่สุด"
				value={bestCategory?.shortLabel ?? '-'}
				subtitle={
					bestCategory
						? `${formatConversionRate(bestCategory.byDay.day7.rate)} (7 วัน)`
						: undefined
				}
				icon={<TrendingUp className="h-5 w-5" />}
				isLoading={isLoading}
			/>
		</div>
	)
}
